import { useEffect, useState } from 'react';
import { extensionPresence, extensionStoreUrl, type ExtensionPresence } from '../lib/extensionBridge';
import { Button } from './ui';

const DISMISS_KEY = 'lienguard.extensionPromptDismissed';

function dismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * "Add to Chrome" for the property-lookup extension. `card` is the Settings
 * version, always shown; `banner` is the New Job nudge, shown only while the
 * extension is missing and until dismissed.
 */
export default function ExtensionPrompt({ variant }: { variant: 'card' | 'banner' }) {
  const [presence, setPresence] = useState<ExtensionPresence | null>(null);
  const [hidden, setHidden] = useState(variant === 'banner' && dismissed());
  const store = extensionStoreUrl();

  useEffect(() => {
    let live = true;
    void extensionPresence().then((p) => live && setPresence(p));
    return () => {
      live = false;
    };
  }, []);

  if (!presence) return null;

  if (variant === 'banner') {
    if (hidden || presence.state !== 'missing' || !store) return null;
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-tint/40 bg-tint/10 px-4 py-3">
        <p className="text-sm text-ink">
          <strong>Skip the typing.</strong> The LienGuardian Chrome extension fills in the owner, mailing address and
          legal description from any Florida county appraiser’s page.
        </p>
        <div className="flex items-center gap-2">
          <a href={store} target="_blank" rel="noreferrer">
            <Button>Add to Chrome</Button>
          </a>
          <button
            type="button"
            className="text-xs font-semibold text-dim hover:text-ink"
            onClick={() => {
              try {
                window.localStorage.setItem(DISMISS_KEY, '1');
              } catch {
                /* private mode: hide for this visit only */
              }
              setHidden(true);
            }}
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted">
        Open a parcel on any Florida county property appraiser’s site, click the LienGuardian icon, check what it read,
        and start the job. It reads a page only when you click it.
      </p>
      {presence.state === 'installed' ? (
        <p className="font-semibold text-ok">Installed in this browser{presence.version ? ` (version ${presence.version})` : ''}.</p>
      ) : presence.state === 'unsupported' ? (
        <p className="text-muted">Works in Chrome, Edge or Brave on a computer. Open LienGuardian there to add it.</p>
      ) : store ? (
        <a href={store} target="_blank" rel="noreferrer">
          <Button>Add to Chrome</Button>
        </a>
      ) : (
        <p className="text-muted">Coming to the Chrome Web Store shortly.</p>
      )}
    </div>
  );
}
